import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Send, Wrench, MessageSquare, Camera, CheckCircle2 } from 'lucide-react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/SiteContexts';
import { cn } from '../../lib/utils';
import toast from 'react-hot-toast';

interface CustomInstrumentModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CustomInstrumentModal: React.FC<CustomInstrumentModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    details: '',
    material: 'Stainless Steel',
    quantity: '1',
    urgency: 'standard'
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      toast.error("Please login to submit a request");
      return;
    }

    setLoading(true);
    try {
      await addDoc(collection(db, 'custom_requests'), {
        ...formData,
        userId: user.uid,
        userEmail: user.email,
        status: 'PENDING',
        type: 'CUSTOM_INSTRUMENT',
        createdAt: serverTimestamp()
      });
      setSubmitted(true);
      toast.success("Request submitted successfully!");
    } catch (err: any) {
      handleFirestoreError(err, OperationType.CREATE, 'custom_requests');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[250] flex items-center justify-center p-4">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-slate-950/60 backdrop-blur-md"
          />
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-2xl bg-white rounded-[2.5rem] shadow-2xl overflow-hidden"
          >
            <button 
              onClick={onClose}
              className="absolute top-8 right-8 p-2 text-slate-400 hover:text-slate-950 transition-colors z-10"
            >
              <X size={24} />
            </button>

            {submitted ? (
              <div className="p-16 text-center">
                <div className="w-20 h-20 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-8">
                  <CheckCircle2 size={40} />
                </div>
                <h2 className="text-3xl font-black text-slate-900 uppercase tracking-tight mb-4">Request Sent!</h2>
                <p className="text-slate-500 mb-8">Our expert engineers will review your custom instrument specifications and get back to you within 24-48 hours.</p>
                <button 
                  onClick={onClose}
                  className="px-12 py-4 bg-indigo-600 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-slate-950 transition-all shadow-xl shadow-indigo-100"
                >
                  Close
                </button>
              </div>
            ) : (
              <div className="p-12">
                <div className="flex items-center gap-4 mb-8">
                  <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl">
                    <Wrench size={24} />
                  </div>
                  <div>
                    <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Custom Fabrication</h2>
                    <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Bespoke Surgical Solutions</p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Instrument Title</label>
                      <input 
                        required
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-bold text-slate-700"
                        placeholder="e.g. Curved Laparoscopic Grasper"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Preferred Material</label>
                      <select 
                        value={formData.material}
                        onChange={(e) => setFormData({...formData, material: e.target.value})}
                        className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-bold text-slate-700 appearance-none"
                      >
                        <option>Stainless Steel (316L)</option>
                        <option>Titanium Grade 5</option>
                        <option>Tungsten Carbide Inlay</option>
                        <option>Special Alloy</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Technical Specifications & Notes</label>
                    <textarea 
                      required
                      value={formData.details}
                      onChange={(e) => setFormData({...formData, details: e.target.value})}
                      rows={4}
                      className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-bold text-slate-700"
                      placeholder="Please provide dimensions, curve angles, and any specific clinical requirements..."
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Batch Quantity</label>
                      <input 
                        type="number"
                        min="1"
                        value={formData.quantity}
                        onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                        className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-bold text-slate-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Urgency Level</label>
                      <div className="flex gap-2">
                        {['standard', 'express'].map((lvl) => (
                          <button
                            key={lvl}
                            type="button"
                            onClick={() => setFormData({...formData, urgency: lvl})}
                            className={cn(
                              "flex-1 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all border",
                              formData.urgency === lvl ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-100 text-slate-500"
                            )}
                          >
                            {lvl}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <button 
                    disabled={loading}
                    className="w-full py-5 bg-indigo-600 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-slate-950 transition-all shadow-xl shadow-indigo-100 flex items-center justify-center gap-3 disabled:opacity-50"
                  >
                    {loading ? "Processing..." : (
                      <>
                        Submit Specification
                        <Send size={18} />
                      </>
                    )}
                  </button>
                </form>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};